import {
  accItemMetadataPathCandidates,
  accLineageItemIdForDelete,
  accVersionDetailPathCandidates,
  accVersionsListPathCandidates,
  normalizeDataManagementItemId,
} from "./accItemIds";
import { dmFetch, isRetryableAccItemPathError } from "./client";
import {
  downloadObjectViaSignedS3,
  parseObjectStorageUrn,
  uploadBytesToSignedOss,
} from "./ossUpload";
import { type DmAdsRegion, dmRegionFromHub } from "./region";
import type {
  AccFolderEntry,
  AccHub,
  AccHubKind,
  AccItemVersion,
  AccProject,
  AccProjectDetails,
  DmCollection,
  DmResource,
} from "./types";
import { fetchWorkspaceAccHubs, mergeAccHubs } from "./workspaceHubs";

function extensionType(resource: DmResource): string {
  const ext = resource.attributes?.extension;
  if (ext && typeof ext === "object" && ext !== null && "type" in ext) {
    return String((ext as { type?: string }).type ?? "");
  }
  return "";
}

function hubKind(extensionType: string): AccHubKind {
  const t = extensionType.toLowerCase();
  if (t.includes("bim360") || t.includes("acc")) return "acc";
  return "other";
}

function resourceName(resource: DmResource, fallback = "Unnamed"): string {
  const attrs = resource.attributes ?? {};
  const displayName = attrs.displayName;
  if (typeof displayName === "string" && displayName.length > 0) {
    return displayName;
  }
  const name = attrs.name;
  return typeof name === "string" && name.length > 0 ? name : fallback;
}

function extensionFromFileName(fileName: string): string | undefined {
  const lower = fileName.toLowerCase();
  const idx = lower.lastIndexOf(".");
  if (idx <= 0 || idx === lower.length - 1) return undefined;
  return lower.slice(idx);
}

function fileExtension(resource: DmResource): string | undefined {
  const fromName = extensionFromFileName(resourceName(resource));
  if (fromName) return fromName;

  const fileType = resource.attributes?.fileType;
  if (typeof fileType === "string" && fileType.length > 0) {
    const ft = fileType.toLowerCase();
    return ft.startsWith(".") ? ft : `.${ft}`;
  }

  const ext = resource.attributes?.extension;
  if (ext && typeof ext === "object" && ext !== null) {
    const data = (ext as { data?: { sourceFileName?: string } }).data;
    const source = data?.sourceFileName;
    if (typeof source === "string") {
      return extensionFromFileName(source);
    }
  }

  return undefined;
}

function encodePathSegment(id: string): string {
  return encodeURIComponent(id);
}

function storageUrnFromVersionResource(
  version: DmResource
): string | undefined {
  const rels = version.relationships as
    | { storage?: { data?: { id?: string } } }
    | undefined;
  const id = rels?.storage?.data?.id;
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

/**
 * Browser-side APS Data Management client for ACC / Forma project files.
 * Mirrors Tyto's `DataManagementService` surface; calls APS REST directly
 * with the signed-in user's PKCE token (no backend proxy).
 */
export class DataManagementService {
  constructor(
    private readonly ensureAccessToken: () => Promise<string | null>
  ) {}

  private async token(): Promise<string> {
    const t = await this.ensureAccessToken();
    if (!t) throw new Error("Not signed in — APS access token required.");
    return t;
  }

  /** Hubs the user can access. ACC/BIM360 hubs are tagged `kind: "acc"`. */
  async getHubs(): Promise<AccHub[]> {
    const json = await dmFetch<DmCollection<DmResource>>(
      await this.token(),
      "/project/v1/hubs"
    );
    return (json.data ?? []).map(h => {
      const ext = extensionType(h);
      return {
        id: h.id,
        name: resourceName(h, "Hub"),
        kind: hubKind(ext),
        extensionType: ext,
        region:
          typeof h.attributes?.region === "string"
            ? h.attributes.region
            : undefined,
      };
    });
  }

  async getAccHubs(): Promise<AccHub[]> {
    const token = await this.token();
    const fromDm = (await this.getHubs()).filter(h => h.kind === "acc");
    const dmIds = new Set(fromDm.map(h => h.id));
    // Always merge Workspace hubs: DM only lists accounts where the APS app is
    // provisioned, so a single provisioned hub (e.g. internal testing) must not
    // hide other entitled hubs the user sees in Forma.
    const fromWorkspace = await fetchWorkspaceAccHubs(token);
    const merged = mergeAccHubs(fromDm, fromWorkspace);
    return merged
      .map(h => ({ ...h, dmProvisioned: dmIds.has(h.id) }))
      .sort((a, b) => {
        const aRank = a.dmProvisioned ? 0 : 1;
        const bRank = b.dmProvisioned ? 0 : 1;
        if (aRank !== bRank) return aRank - bRank;
        return a.name.localeCompare(b.name);
      });
  }

  async getHubProjects(
    hubId: string,
    hubRegion?: string
  ): Promise<AccProject[]> {
    if (!hubId.trim()) throw new Error("hubId is required");
    const region = dmRegionFromHub(hubRegion);
    const json = await dmFetch<DmCollection<DmResource>>(
      await this.token(),
      `/project/v1/hubs/${encodePathSegment(hubId)}/projects`,
      { region }
    );
    return (json.data ?? []).map(p => ({
      id: p.id,
      hubId,
      name: resourceName(p, "Project"),
    }));
  }

  async getProject(
    hubId: string,
    projectId: string,
    hubRegion?: string
  ): Promise<AccProjectDetails> {
    if (!hubId.trim() || !projectId.trim()) {
      throw new Error("hubId and projectId are required");
    }
    const region = dmRegionFromHub(hubRegion);
    const json = await dmFetch<{ data: DmResource }>(
      await this.token(),
      `/project/v1/hubs/${encodePathSegment(hubId)}/projects/${encodePathSegment(projectId)}`,
      { region }
    );
    const p = json.data;
    const ext = p.attributes?.extension;
    const extData =
      ext && typeof ext === "object" && ext !== null && "data" in ext
        ? (ext as { data?: Record<string, unknown> }).data
        : undefined;
    const platform =
      typeof extData?.projectType === "string"
        ? extData.projectType
        : undefined;
    const status =
      typeof p.attributes?.status === "string"
        ? p.attributes.status
        : undefined;
    return {
      id: p.id,
      hubId,
      name: resourceName(p, "Project"),
      platform,
      status,
    };
  }

  async getProjectTopFolders(
    hubId: string,
    projectId: string,
    hubRegion?: string
  ): Promise<AccFolderEntry[]> {
    if (!hubId.trim() || !projectId.trim()) {
      throw new Error("hubId and projectId are required");
    }
    const region = dmRegionFromHub(hubRegion);
    const json = await dmFetch<DmCollection<DmResource>>(
      await this.token(),
      `/project/v1/hubs/${encodePathSegment(hubId)}/projects/${encodePathSegment(projectId)}/topFolders`,
      { region }
    );
    return mapFolderContents(json.data ?? []);
  }

  async getFolderContents(
    projectId: string,
    folderId: string,
    hubRegion?: string
  ): Promise<AccFolderEntry[]> {
    if (!projectId.trim() || !folderId.trim()) {
      throw new Error("projectId and folderId are required");
    }
    const region = dmRegionFromHub(hubRegion);
    const json = await dmFetch<DmCollection<DmResource>>(
      await this.token(),
      `/data/v1/projects/${encodePathSegment(projectId)}/folders/${encodePathSegment(folderId)}/contents?page[limit]=200`,
      { region }
    );
    return mapFolderContents(json.data ?? []);
  }

  async getItemVersions(
    projectId: string,
    itemId: string,
    hubRegion?: string
  ): Promise<AccItemVersion[]> {
    if (!projectId.trim() || !itemId.trim()) {
      throw new Error("projectId and itemId are required");
    }
    const region = dmRegionFromHub(hubRegion);
    const token = await this.token();
    const encProject = encodePathSegment(projectId);
    let lastErr: unknown;

    for (const dmItemId of accVersionsListPathCandidates(itemId)) {
      try {
        const json = await dmFetch<DmCollection<DmResource>>(
          token,
          `/data/v1/projects/${encProject}/items/${encodePathSegment(dmItemId)}/versions`,
          { region }
        );
        return (json.data ?? []).map(v => {
          const rels = v.relationships as
            | { derivatives?: { data?: { id?: string } } }
            | undefined;
          const derivativeUrn = rels?.derivatives?.data?.id;
          const createTime =
            typeof v.attributes?.createTime === "string"
              ? v.attributes.createTime
              : undefined;
          return {
            id: v.id,
            name: resourceName(v, "Version"),
            derivativeUrn:
              typeof derivativeUrn === "string" ? derivativeUrn : undefined,
            createTime,
            storageUrn: storageUrnFromVersionResource(v),
          };
        });
      } catch (e) {
        lastErr = e;
        if (isRetryableAccItemPathError(e)) continue;
        throw e;
      }
    }

    if (lastErr instanceof Error) throw lastErr;
    throw new Error("No versions found for this file.");
  }

  /** Latest file bytes for package PDFs / sidecar downloads (PKCE token). */
  async downloadItemBlob(
    projectId: string,
    itemId: string,
    hubRegion?: string
  ): Promise<Blob> {
    const versions = await this.getItemVersions(projectId, itemId, hubRegion);
    const version = pickLatestVersion(versions);
    if (!version) {
      throw new Error("No versions found for this file.");
    }
    const token = await this.token();
    const region = dmRegionFromHub(hubRegion);

    if (version.storageUrn) {
      return downloadObjectViaSignedS3(token, version.storageUrn, region);
    }

    const storageUrn = await this.resolveVersionStorageUrn(
      token,
      projectId,
      itemId,
      version.id,
      region
    );
    if (storageUrn) {
      return downloadObjectViaSignedS3(token, storageUrn, region);
    }

    const downloadUrl = await this.resolveItemDownloadUrl(
      token,
      projectId,
      itemId,
      version.id,
      region
    );
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (region) headers["x-ads-region"] = region;
    const res = await fetch(downloadUrl, { headers });
    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`);
    }
    return res.blob();
  }

  private async resolveVersionStorageUrn(
    token: string,
    projectId: string,
    itemId: string,
    versionId: string,
    region?: DmAdsRegion
  ): Promise<string | undefined> {
    const encProject = encodePathSegment(projectId);
    const encVersion = encodePathSegment(versionId);

    for (const dmItemId of accVersionDetailPathCandidates(itemId, versionId)) {
      try {
        const versionJson = await dmFetch<{ data?: DmResource }>(
          token,
          `/data/v1/projects/${encProject}/items/${encodePathSegment(dmItemId)}/versions/${encVersion}`,
          { region }
        );
        if (versionJson.data) {
          const urn = storageUrnFromVersionResource(versionJson.data);
          if (urn) return urn;
        }
      } catch (e) {
        if (isRetryableAccItemPathError(e)) continue;
      }
    }
    return undefined;
  }

  private async resolveItemDownloadUrl(
    token: string,
    projectId: string,
    itemId: string,
    versionId: string,
    region?: DmAdsRegion
  ): Promise<string> {
    const encProject = encodePathSegment(projectId);
    const encVersion = encodePathSegment(versionId);
    let lastErr: unknown;

    for (const dmItemId of accVersionDetailPathCandidates(itemId, versionId)) {
      const encItem = encodePathSegment(dmItemId);
      const formatsPath = `/data/v1/projects/${encProject}/items/${encItem}/versions/${encVersion}/downloadFormats`;
      try {
        const formatsJson = await dmFetch<{
          data?: {
            attributes?: {
              formats?: Array<{ downloadUrl?: string; format?: string }>;
            };
          };
        }>(token, formatsPath, { region });
        const formats = formatsJson.data?.attributes?.formats ?? [];
        const original =
          formats.find(f => f.format?.toUpperCase() === "ORIGINAL") ??
          formats[0];
        if (original?.downloadUrl) return original.downloadUrl;
      } catch (e) {
        lastErr = e;
        if (!isRetryableAccItemPathError(e)) {
          // keep trying other item id shapes
        }
      }

      try {
        const versionJson = await dmFetch<{ data?: DmResource }>(
          token,
          `/data/v1/projects/${encProject}/items/${encItem}/versions/${encVersion}`,
          { region }
        );
        const rels = versionJson.data?.relationships as
          | { storage?: { meta?: { link?: { href?: string } } } }
          | undefined;
        const href = rels?.storage?.meta?.link?.href;
        if (typeof href === "string" && href.length > 0) return href;
      } catch (e) {
        lastErr = e;
        if (isRetryableAccItemPathError(e)) continue;
      }
    }

    if (lastErr instanceof Error) throw lastErr;
    throw new Error("Could not resolve a download URL for this file.");
  }

  /**
   * Soft-delete an ACC Docs file (moves to Deleted Items) via a Deleted version.
   * Hard DELETE on `/items` is not supported for BIM360/ACC.
   */
  async softDeleteProjectItem(
    projectId: string,
    itemId: string,
    fileName: string,
    hubRegion?: string
  ): Promise<void> {
    if (!projectId.trim() || !itemId.trim()) {
      throw new Error("projectId and itemId are required");
    }
    const token = await this.token();
    const region = dmRegionFromHub(hubRegion);
    const encProject = encodePathSegment(projectId);
    const lineageId = accLineageItemIdForDelete(itemId);
    const safeName = fileName.replace(/[/\\]/g, "_").trim() || "document.pdf";

    await dmFetch<unknown>(token, `/data/v1/projects/${encProject}/versions`, {
      region,
      init: {
        method: "POST",
        body: JSON.stringify({
          jsonapi: { version: "1.0" },
          data: {
            type: "versions",
            attributes: {
              name: safeName,
              extension: {
                type: "versions:autodesk.core:Deleted",
                version: "1.0",
              },
            },
            relationships: {
              item: { data: { type: "items", id: lineageId } },
            },
          },
        }),
      },
    });
  }

  /**
   * Upload a new version on an existing ACC file (replace PDF in place).
   * Requires PKCE scopes `data:create` and `data:write`.
   */
  async uploadNewVersionToItem(
    projectId: string,
    itemId: string,
    file: File,
    hubRegion?: string
  ): Promise<{ itemId: string; fileName: string }> {
    if (!projectId.trim() || !itemId.trim()) {
      throw new Error("projectId and itemId are required");
    }
    const safeName = file.name.replace(/[/\\]/g, "_").trim();
    if (!safeName) throw new Error("File name is required");

    const token = await this.token();
    const region = dmRegionFromHub(hubRegion);
    const encProject = encodePathSegment(projectId);
    const dmItemId = normalizeDataManagementItemId(itemId);

    const folderId = await this.getItemParentFolderId(
      token,
      projectId,
      dmItemId,
      region
    );

    const objectId = await this.createStorageObjectForFolder(
      token,
      encProject,
      folderId,
      safeName,
      file,
      region
    );

    await dmFetch<{ data?: { id?: string } }>(
      token,
      `/data/v1/projects/${encProject}/versions`,
      {
        region,
        init: {
          method: "POST",
          body: JSON.stringify({
            jsonapi: { version: "1.0" },
            data: {
              type: "versions",
              attributes: {
                name: safeName,
                extension: {
                  type: "versions:autodesk.bim360:File",
                  version: "1.0",
                },
              },
              relationships: {
                item: { data: { type: "items", id: dmItemId } },
                storage: { data: { type: "objects", id: objectId } },
              },
            },
          }),
        },
      }
    );

    return { itemId: dmItemId, fileName: safeName };
  }

  /**
   * Upload a new file into an ACC folder (storage → OSS → DM item).
   * Requires PKCE scopes `data:create` and `data:write`.
   */
  async uploadFileToFolder(
    projectId: string,
    folderId: string,
    file: File,
    hubRegion?: string
  ): Promise<{ itemId: string; fileName: string }> {
    if (!projectId.trim() || !folderId.trim()) {
      throw new Error("projectId and folderId are required");
    }
    const safeName = file.name.replace(/[/\\]/g, "_").trim();
    if (!safeName) throw new Error("File name is required");

    const token = await this.token();
    const region = dmRegionFromHub(hubRegion);
    const encProject = encodePathSegment(projectId);

    const objectId = await this.createStorageObjectForFolder(
      token,
      encProject,
      folderId,
      safeName,
      file,
      region
    );

    const versionTempId = "1";
    const itemJson = await dmFetch<{ data?: { id?: string } }>(
      token,
      `/data/v1/projects/${encProject}/items`,
      {
        region,
        init: {
          method: "POST",
          body: JSON.stringify({
            jsonapi: { version: "1.0" },
            data: {
              type: "items",
              attributes: {
                displayName: safeName,
                extension: {
                  type: "items:autodesk.bim360:File",
                  version: "1.0",
                },
              },
              relationships: {
                tip: { data: { type: "versions", id: versionTempId } },
                parent: { data: { type: "folders", id: folderId } },
              },
            },
            included: [
              {
                type: "versions",
                id: versionTempId,
                attributes: {
                  name: safeName,
                  extension: {
                    type: "versions:autodesk.bim360:File",
                    version: "1.0",
                  },
                },
                relationships: {
                  storage: { data: { type: "objects", id: objectId } },
                },
              },
            ],
          }),
        },
      }
    );

    const itemId = itemJson.data?.id;
    if (!itemId) throw new Error("APS did not return a new item id.");

    return { itemId, fileName: safeName };
  }

  private async getItemParentFolderId(
    token: string,
    projectId: string,
    itemId: string,
    region?: DmAdsRegion
  ): Promise<string> {
    const encProject = encodePathSegment(projectId);
    for (const dmItemId of accItemMetadataPathCandidates(itemId)) {
      try {
        const itemJson = await dmFetch<{ data?: DmResource }>(
          token,
          `/data/v1/projects/${encProject}/items/${encodePathSegment(dmItemId)}`,
          { region }
        );
        const rels = itemJson.data?.relationships as
          | { parent?: { data?: { id?: string; type?: string } } }
          | undefined;
        const parentId = rels?.parent?.data?.id;
        if (typeof parentId === "string" && parentId.length > 0)
          return parentId;
      } catch (e) {
        if (isRetryableAccItemPathError(e)) continue;
        throw e;
      }
    }
    throw new Error("Could not find the ACC folder for this file.");
  }

  private async createStorageObjectForFolder(
    token: string,
    encProject: string,
    folderId: string,
    safeName: string,
    file: File,
    region?: DmAdsRegion
  ): Promise<string> {
    const storageJson = await dmFetch<{ data?: { id?: string } }>(
      token,
      `/data/v1/projects/${encProject}/storage`,
      {
        region,
        init: {
          method: "POST",
          body: JSON.stringify({
            jsonapi: { version: "1.0" },
            data: {
              type: "objects",
              attributes: { name: safeName },
              relationships: {
                target: { data: { type: "folders", id: folderId } },
              },
            },
          }),
        },
      }
    );

    const objectId = storageJson.data?.id;
    if (!objectId) throw new Error("APS did not return a storage object id.");

    const { bucketKey, objectKey } = parseObjectStorageUrn(objectId);
    const bytes = await file.arrayBuffer();
    const contentType =
      file.type && file.type.length > 0
        ? file.type
        : "application/octet-stream";

    await uploadBytesToSignedOss(
      token,
      bucketKey,
      objectKey,
      bytes,
      contentType,
      region
    );

    return objectId;
  }
}

function pickLatestVersion(
  versions: AccItemVersion[]
): AccItemVersion | undefined {
  if (versions.length === 0) return undefined;
  return [...versions].sort((a, b) => {
    const ta = a.createTime ? Date.parse(a.createTime) : 0;
    const tb = b.createTime ? Date.parse(b.createTime) : 0;
    return tb - ta;
  })[0];
}

function mapFolderContents(rows: DmResource[]): AccFolderEntry[] {
  return rows
    .filter(r => r.type === "folders" || r.type === "items")
    .map(r => {
      if (r.type === "folders") {
        return {
          id: r.id,
          name: resourceName(r, "Folder"),
          objectType: "folders" as const,
        };
      }
      return {
        id: r.id,
        name: resourceName(r, "Item"),
        objectType: "items" as const,
        extension: fileExtension(r),
      };
    });
}
